import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, addDays, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseLocalDateTime, formatTime, isSameDayLocal } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Calendar } from './ui/calendar';
import { Checkbox } from './ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search,
  X,
  Clock,
  User,
  MapPin,
  CalendarIcon,
  ChevronDown,
  Filter,
  Phone,
  Mail
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Paleta de cores para profissionais (fallback se não tiver cor definida)
const CORES_PROFISSIONAIS = [
  '#4A6B5D', '#E07A5F', '#3D405B', '#81B29A', '#F2CC8F',
  '#6D597A', '#B56576', '#355070', '#EAAC8B', '#457B9D'
];

// Horários de funcionamento
const HORARIO_INICIO = 7;
const HORARIO_FIM = 20;
const INTERVALO_MINUTOS = 30;

const AgendaSemanalView = ({ 
  agendamentos = [], 
  salas = [], 
  profissionais = [],
  loading = false,
  onAgendamentoClick,
  onWeekChange,
  onNovoAgendamento,  // Nova prop para abrir modal de novo agendamento
  isProfissional = false
}) => {
  // No mobile, sempre começar com a data de HOJE
  const [baseDate, setBaseDate] = useState(() => new Date());
  // Desktop: para profissional, começar com hoje; para admin/recepção, começar com segunda
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    isProfissional ? new Date() : startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroSalas, setFiltroSalas] = useState([]); // Multi-seleção de salas
  const [filtroProfissionais, setFiltroProfissionais] = useState([]); // Multi-seleção de profissionais
  const [buscaProfissional, setBuscaProfissional] = useState(''); // Busca no dropdown de profissionais
  const [disponibilidade, setDisponibilidade] = useState({}); // Disponibilidade dos profissionais
  const [pausas, setPausas] = useState([]); // Pausas/intervalos do profissional filtrado
  const [bloqueios, setBloqueios] = useState([]); // Bloqueios de agenda do profissional filtrado
  
  // Hook de autenticação para fazer requisições autenticadas
  const { authFetch } = useAuth();
  
  // Estado para navegação mobile - baseDate é o primeiro dia visível
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Carregar dados quando mudar a semana base
  useEffect(() => {
    // Calcular o range de datas que precisamos buscar
    if (isMobile) {
      // No mobile, buscar a semana que contém baseDate + alguns dias extras
      const weekStart = startOfWeek(baseDate, { weekStartsOn: 1 });
      const weekEnd = addDays(baseDate, 14); // Buscar 2 semanas para cobrir navegação
      onWeekChange?.(weekStart, weekEnd);
    } else if (isProfissional) {
      // Profissional: buscar a partir de currentWeekStart + 6 dias
      const weekEnd = addDays(currentWeekStart, 6);
      onWeekChange?.(currentWeekStart, weekEnd);
    } else {
      // Admin/Recepção: buscar a semana inteira (seg-dom)
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      onWeekChange?.(currentWeekStart, weekEnd);
    }
  }, [currentWeekStart, baseDate, isMobile, isProfissional]);

  // Buscar disponibilidade dos profissionais filtrados
  useEffect(() => {
    const fetchDisponibilidade = async () => {
      if (isProfissional) return;
      if (!authFetch) return;
      
      try {
        if (filtroProfissionais.length !== 1) {
          setDisponibilidade({});
          return;
        }
        
        const url = `/locacoes/disponibilidade-semanal?profissional_id=${filtroProfissionais[0]}`;
        const response = await authFetch(url);
        const novaDisponibilidade = response.data?.disponibilidade || {};
        setDisponibilidade({...novaDisponibilidade});
      } catch (error) {
        console.error('Erro ao buscar disponibilidade:', error);
        setDisponibilidade({});
      }
    };
    
    fetchDisponibilidade();
  }, [filtroProfissionais, isProfissional, authFetch]);

  // Buscar pausas/intervalos do profissional filtrado
  useEffect(() => {
    const fetchPausas = async () => {
      if (!authFetch) return;
      
      try {
        if (filtroProfissionais.length !== 1) {
          setPausas([]);
          return;
        }
        
        const url = `/pausas/profissional/${filtroProfissionais[0]}`;
        const response = await authFetch(url);
        setPausas(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar pausas:', error);
        setPausas([]);
      }
    };
    
    fetchPausas();
  }, [filtroProfissionais, authFetch]);

  // Buscar bloqueios de agenda do profissional filtrado
  useEffect(() => {
    const fetchBloqueios = async () => {
      if (!authFetch) return;
      try {
        if (filtroProfissionais.length !== 1) {
          setBloqueios([]);
          return;
        }
        // Endpoint retorna bloqueios ativos do profissional
        const url = `/bloqueios-agenda?profissional_id=${filtroProfissionais[0]}`;
        const response = await authFetch(url);
        const data = response?.data || response || [];
        setBloqueios(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erro ao buscar bloqueios:', error);
        setBloqueios([]);
      }
    };
    fetchBloqueios();
  }, [filtroProfissionais, authFetch]);

  // Dias visíveis - no mobile são 3 dias a partir de baseDate
  const diasSemana = useMemo(() => {
    if (isMobile) {
      // Mobile: hoje + 2 dias à frente (ou baseDate + 2)
      return [baseDate, addDays(baseDate, 1), addDays(baseDate, 2)];
    }
    if (isProfissional) {
      // Profissional Desktop: 6 dias a partir de currentWeekStart (hoje na primeira coluna)
      return eachDayOfInterval({
        start: currentWeekStart,
        end: addDays(currentWeekStart, 5)
      });
    }
    // Admin/Recepção Desktop: semana completa seg-sáb
    return eachDayOfInterval({
      start: currentWeekStart,
      end: addDays(currentWeekStart, 5)
    });
  }, [currentWeekStart, baseDate, isMobile, isProfissional]);
  
  // Navegação mobile - mover baseDate
  const irDiasAnteriores = () => {
    setBaseDate(prev => addDays(prev, -3));
  };
  
  const irDiasProximos = () => {
    setBaseDate(prev => addDays(prev, 3));
  };

  // Gerar slots de horário
  const horariosSlots = useMemo(() => {
    const slots = [];
    for (let hora = HORARIO_INICIO; hora < HORARIO_FIM; hora++) {
      for (let min = 0; min < 60; min += INTERVALO_MINUTOS) {
        slots.push({
          hora,
          minuto: min,
          label: `${hora.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
        });
      }
    }
    return slots;
  }, []);

  // Mapa de cores por profissional
  const coresProfissionais = useMemo(() => {
    const mapa = {};
    profissionais.forEach((prof, index) => {
      mapa[prof.id] = prof.cor || CORES_PROFISSIONAIS[index % CORES_PROFISSIONAIS.length];
    });
    return mapa;
  }, [profissionais]);

  // Set de usuario_ids que atuam em >1 especialidade DISTINTA — nesses,
  // qualificamos o nome com " — Especialidade".
  const usuariosMultiEsp = useMemo(() => {
    const espsPorUser = {};
    profissionais.forEach(p => {
      const uid = p.usuario_id || p.usuarios?.id;
      const esp = p.especialidade;
      if (!uid || !esp) return;
      if (!espsPorUser[uid]) espsPorUser[uid] = new Set();
      espsPorUser[uid].add(esp);
    });
    return new Set(Object.entries(espsPorUser).filter(([, s]) => s.size > 1).map(([uid]) => uid));
  }, [profissionais]);

  // 2026-08 — Set de usuario_ids que atuam sob >1 "chapéu" (múltiplos registros em
  // profissionais). Cobre tanto multi-especialidade (ex.: Dayene Psico+Musico)
  // quanto duplo-contrato mesma especialidade (ex.: Geovane percentual+locacao).
  // Usado para sufixar o rótulo no filtro/cards e evitar exibir 2 vezes o mesmo nome.
  const usuariosMultiChapeu = useMemo(() => {
    const cnt = {};
    profissionais.forEach(p => {
      const uid = p.usuario_id || p.usuarios?.id;
      if (!uid) return;
      cnt[uid] = (cnt[uid] || 0) + 1;
    });
    return new Set(Object.entries(cnt).filter(([, n]) => n > 1).map(([uid]) => uid));
  }, [profissionais]);

  // Helper: rótulo do profissional. Se ele atua em >1 chapéu, sufixa para desambiguar:
  //  - especialidades distintas → " — Especialidade" (ex.: "Fulvio — Musicoterapia")
  //  - mesma especialidade, modalidades distintas → " — Modalidade" (ex.: "Geovane — Locação")
  //  - fallback (mesma esp+modalidade) → " — Contrato N" para separar visualmente.
  const rotuloProf = (prof) => {
    const nome = prof?.usuarios?.nome || prof?.nome || 'Profissional';
    const uid = prof?.usuario_id || prof?.usuarios?.id;
    if (!uid) return nome;

    // Caso 1: multi-especialidade
    if (usuariosMultiEsp.has(uid) && prof?.especialidade) {
      return `${nome} — ${prof.especialidade}`;
    }

    // Caso 2: duplo-contrato (mesma esp). Discriminador = modalidade_agenda.
    if (usuariosMultiChapeu.has(uid)) {
      const mod = prof?.modalidade_agenda;
      if (mod === 'locacao') return `${nome} — Locação`;
      if (mod === 'percentual') return `${nome} — Percentual`;
      // Fallback quando modalidade_agenda não está preenchida em nenhum
      const modelo = prof?.modelo_contrato;
      if (modelo === 'sublocacao') return `${nome} — Sublocação`;
      if (modelo === 'percentual') return `${nome} — Percentual`;
      // Último recurso: sufixa pelos 4 primeiros chars do prof_id para distinguir
      return `${nome} — Contrato ${(prof.id || '').slice(0, 4)}`;
    }

    return nome;
  };

  // Profissionais filtrados pela busca no dropdown (busca por nome, especialidade
  // ou modalidade — locação/percentual/sublocação).
  const profissionaisFiltrados = useMemo(() => {
    if (!buscaProfissional.trim()) return profissionais;
    const termo = buscaProfissional.toLowerCase();
    return profissionais.filter(prof => {
      const nome = (prof.usuarios?.nome || prof.nome || '').toLowerCase();
      const esp = (prof.especialidade || '').toLowerCase();
      const mod = (prof.modalidade_agenda || prof.modelo_contrato || '').toLowerCase();
      return nome.includes(termo) || esp.includes(termo) || mod.includes(termo);
    });
  }, [profissionais, buscaProfissional]);

  // Verificar se um horário está disponível para um profissional em um dia específico
  const getSlotDisponibilidade = (dia, hora, minuto = 0) => {
    const diaSemana = getDay(dia);
    
    if (filtroProfissionais.length !== 1) {
      return { disponivel: null, slot: null };
    }
    
    const profId = filtroProfissionais[0];
    const profDisp = disponibilidade[profId];
    
    if (!profDisp || !profDisp.dias) {
      return { disponivel: null, slot: null };
    }
    
    // As chaves de 'dias' vêm como strings do JSON ("1", "3", etc)
    const diaSemanaStr = String(diaSemana);
    if (!profDisp.dias[diaSemanaStr]) {
      return { disponivel: false, slot: null };
    }
    
    const slots = profDisp.dias[diaSemanaStr];
    const horaStr = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}:00`;
    
    for (const slot of slots) {
      if (!slot.horario_inicio || !slot.horario_fim) continue;
      
      if (horaStr >= slot.horario_inicio && horaStr < slot.horario_fim) {
        return { disponivel: true, slot };
      }
    }
    
    return { disponivel: false, slot: null };
  };

  // Verificar se um horário está dentro de uma pausa/intervalo
  const getSlotPausa = (dia, hora, minuto = 0) => {
    if (pausas.length === 0) return null;
    
    const diaSemana = getDay(dia);
    const horaStr = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
    
    for (const pausa of pausas) {
      // Verificar se a pausa se aplica a este dia
      if (pausa.dia_semana !== null && pausa.dia_semana !== diaSemana) {
        continue;
      }
      
      const pausaInicio = (pausa.hora_inicio || '00:00').slice(0, 5);
      const pausaFim = (pausa.hora_fim || '00:00').slice(0, 5);
      
      // Verificar se o horário está dentro da pausa
      if (horaStr >= pausaInicio && horaStr < pausaFim) {
        return pausa;
      }
    }
    
    return null;
  };

  // Verificar se um horário está dentro de um bloqueio de agenda
  const getSlotBloqueio = (dia, hora, minuto = 0) => {
    if (bloqueios.length === 0) return null;
    const diaStr = format(dia, 'yyyy-MM-dd');
    const horaStr = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
    for (const b of bloqueios) {
      const inicio = b.data_inicio;
      const fim = b.data_fim;
      // Verifica se o dia cai dentro do intervalo do bloqueio
      if (inicio && diaStr < inicio) continue;
      if (fim && diaStr > fim) continue;
      // Se dia_inteiro, todo o dia está bloqueado
      if (b.dia_inteiro) return b;
      // Senão, verifica hora
      const hi = (b.hora_inicio || '00:00').slice(0, 5);
      const hf = (b.hora_fim || '23:59').slice(0, 5);
      if (horaStr >= hi && horaStr < hf) return b;
    }
    return null;
  };
  const handleSlotClick = (dia, hora, minuto = 0) => {
    if (!onNovoAgendamento) return;
    
    // Bloqueia clique em slot com bloqueio de agenda ativo
    if (getSlotBloqueio(dia, hora, minuto)) return;
    
    const { disponivel, slot } = getSlotDisponibilidade(dia, hora, minuto);
    if (disponivel === false) return; // Não permite clicar em slot indisponível
    
    // Preparar dados para o novo agendamento
    const dataHora = new Date(dia);
    dataHora.setHours(hora, minuto, 0, 0);
    
    onNovoAgendamento({
      data_hora_inicio: dataHora.toISOString(),
      profissional_id: filtroProfissionais.length === 1 ? filtroProfissionais[0] : null,
      sala_id: slot?.sala_id || null,
      sala: slot?.sala || null
    });
  };

  // Filtrar agendamentos
  const agendamentosFiltrados = useMemo(() => {
    let filtered = agendamentos;

    // Filtro por busca de paciente
    if (searchTerm.trim()) {
      const termo = searchTerm.toLowerCase();
      filtered = filtered.filter(ag => 
        ag.pacientes?.nome_completo?.toLowerCase().includes(termo) ||
        ag.profissionais?.usuarios?.nome?.toLowerCase().includes(termo)
      );
    }

    // Filtro por salas (multi-seleção)
    if (filtroSalas.length > 0) {
      filtered = filtered.filter(ag => {
        // Se o agendamento tem sala_id direto, usa ele
        if (ag.sala_id) {
          return filtroSalas.includes(ag.sala_id);
        }
        // Se não tem sala_id, busca a sala pela locação do profissional
        // O agendamento pode ter salas.id populado via join
        if (ag.salas?.id) {
          return filtroSalas.includes(ag.salas.id);
        }
        return false;
      });
    }

    // Filtro por profissionais (multi-seleção)
    if (filtroProfissionais.length > 0) {
      filtered = filtered.filter(ag => filtroProfissionais.includes(ag.profissional_id));
    }

    return filtered;
  }, [agendamentos, searchTerm, filtroSalas, filtroProfissionais]);

  // Função para toggle sala no filtro
  const toggleFiltroSala = (salaId) => {
    setFiltroSalas(prev => 
      prev.includes(salaId) 
        ? prev.filter(id => id !== salaId)
        : [...prev, salaId]
    );
  };

  // Função para toggle profissional no filtro
  const toggleFiltroProfissional = (profId) => {
    setFiltroProfissionais(prev => 
      prev.includes(profId) 
        ? prev.filter(id => id !== profId)
        : [...prev, profId]
    );
  };

  // Limpar todos os filtros
  const limparFiltros = () => {
    setFiltroSalas([]);
    setFiltroProfissionais([]);
    setSearchTerm('');
  };

  // Agrupar agendamentos por dia e sala
  const agendamentosPorDiaSala = useMemo(() => {
    const mapa = {};
    
    diasSemana.forEach(dia => {
      const diaKey = format(dia, 'yyyy-MM-dd');
      mapa[diaKey] = {};
      
      salas.forEach(sala => {
        mapa[diaKey][sala.id] = [];
      });
    });

    agendamentosFiltrados.forEach(ag => {
      const inicio = parseLocalDateTime(ag.data_hora_inicio);
      const diaKey = format(inicio, 'yyyy-MM-dd');
      const salaId = ag.sala_id || 'sem_sala';
      
      if (mapa[diaKey]) {
        if (!mapa[diaKey][salaId]) {
          mapa[diaKey][salaId] = [];
        }
        mapa[diaKey][salaId].push(ag);
      }
    });

    return mapa;
  }, [agendamentosFiltrados, diasSemana, salas]);

  // Navegar semanas (desktop)
  const irParaSemanaAnterior = () => {
    if (isMobile) {
      setBaseDate(prev => addDays(prev, -7));
    } else {
      const novaSemana = subWeeks(currentWeekStart, 1);
      setCurrentWeekStart(novaSemana);
    }
  };

  const irParaProximaSemana = () => {
    if (isMobile) {
      setBaseDate(prev => addDays(prev, 7));
    } else {
      const novaSemana = addWeeks(currentWeekStart, 1);
      setCurrentWeekStart(novaSemana);
    }
  };

  const irParaHoje = () => {
    const hoje = new Date();
    if (isMobile) {
      setBaseDate(hoje);
    } else if (isProfissional) {
      // Profissional: hoje na primeira coluna
      setCurrentWeekStart(hoje);
    } else {
      // Admin/Recepção: segunda-feira da semana atual
      const inicioSemana = startOfWeek(hoje, { weekStartsOn: 1 });
      setCurrentWeekStart(inicioSemana);
    }
  };

  // Ir para uma data específica (via calendar picker)
  const irParaData = (data) => {
    if (isMobile) {
      setBaseDate(data);
    } else if (isProfissional) {
      // Profissional: data selecionada na primeira coluna
      setCurrentWeekStart(data);
    } else {
      // Admin/Recepção: segunda-feira da semana da data
      const inicioSemana = startOfWeek(data, { weekStartsOn: 1 });
      setCurrentWeekStart(inicioSemana);
    }
  };

  // Calcular posição e altura do bloco de agendamento
  const calcularEstiloBloco = (agendamento) => {
    const inicio = parseLocalDateTime(agendamento.data_hora_inicio);
    const fim = parseLocalDateTime(agendamento.data_hora_fim);
    
    const horaInicio = inicio.getHours();
    const minInicio = inicio.getMinutes();
    const horaFim = fim.getHours();
    const minFim = fim.getMinutes();
    
    // Posição top baseada no horário de início
    const minutosDesdeInicio = (horaInicio - HORARIO_INICIO) * 60 + minInicio;
    const top = (minutosDesdeInicio / INTERVALO_MINUTOS) * 40; // 40px por slot
    
    // Altura baseada na duração
    const duracaoMinutos = (horaFim - horaInicio) * 60 + (minFim - minInicio);
    const height = Math.max((duracaoMinutos / INTERVALO_MINUTOS) * 40, 30);
    
    return { top: `${top}px`, height: `${height}px` };
  };

  // Renderizar bloco de agendamento
  const renderAgendamento = (agendamento) => {
    const estilo = calcularEstiloBloco(agendamento);
    const cor = coresProfissionais[agendamento.profissional_id] || '#4A6B5D';
    const inicio = parseLocalDateTime(agendamento.data_hora_inicio);
    const fim = parseLocalDateTime(agendamento.data_hora_fim);
    
    const statusCores = {
      agendado: 'border-l-4',
      confirmado: 'border-l-4 opacity-90',
      em_atendimento: 'border-l-4 ring-2 ring-offset-1',
      concluido: 'border-l-4 opacity-60',
      cancelado: 'border-l-4 opacity-40 line-through',
      falta: 'border-l-4 opacity-40'
    };

    return (
      <TooltipProvider key={agendamento.id}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`absolute left-1 right-1 rounded-md px-2 py-1 cursor-pointer pointer-events-auto
                         hover:shadow-lg transition-all overflow-hidden
                         ${statusCores[agendamento.status] || 'border-l-4'}`}
              style={{
                ...estilo,
                backgroundColor: `${cor}20`,
                borderLeftColor: cor,
              }}
              onClick={() => onAgendamentoClick?.(agendamento)}
            >
              <div className="text-xs font-semibold truncate" style={{ color: cor }}>
                {agendamento.pacientes?.nome_completo?.split(' ')[0] || 'Paciente'}
              </div>
              <div className="text-[10px] text-gray-600 truncate">
                {format(inicio, 'HH:mm')} - {format(fim, 'HH:mm')}
              </div>
              {agendamento.is_reposicao && (
                <div
                  data-testid={`badge-reposicao-${agendamento.id}`}
                  className="absolute top-0 right-0 text-[8px] font-bold px-1 py-0.5 rounded-bl-md"
                  style={{ backgroundColor: '#7C3AED', color: 'white' }}
                  title="Reposição pontual"
                >
                  REP
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-semibold">{agendamento.pacientes?.nome_completo}</p>
              <p className="text-sm flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(inicio, 'HH:mm')} - {format(fim, 'HH:mm')}
              </p>
              <p className="text-sm flex items-center gap-1">
                <User className="h-3 w-3" />
                {rotuloProf(agendamento.profissionais) || 'Profissional'}
              </p>
              {agendamento.salas && (
                <p className="text-sm flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {agendamento.salas.nome}
                </p>
              )}
              {agendamento.pacientes?.telefone_responsavel && (
                <p className="text-sm flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {agendamento.pacientes.telefone_responsavel}
                </p>
              )}
              {agendamento.pacientes?.email_responsavel && (
                <p className="text-sm flex items-center gap-1 break-all">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{agendamento.pacientes.email_responsavel}</span>
                </p>
              )}
              <Badge variant="outline" className="text-xs">
                {agendamento.tipo_atendimento === 'particular' 
                  ? 'Particular' 
                  : agendamento.convenios?.nome || agendamento.convenio_nome || 'Convênio'}
              </Badge>
              {agendamento.is_reposicao && (
                <Badge
                  variant="secondary"
                  className="text-xs ml-1"
                  style={{ backgroundColor: '#EDE9FE', color: '#5B21B6' }}
                >
                  REPOSIÇÃO
                </Badge>
              )}
              <Badge 
                variant="secondary" 
                className="text-xs ml-1"
                style={{ 
                  backgroundColor: agendamento.status === 'finalizado' ? '#dcfce7' :
                                   agendamento.status === 'cancelado' ? '#fee2e2' :
                                   agendamento.status === 'falta' ? '#fef3c7' :
                                   agendamento.status === 'em_atendimento' ? '#dbeafe' :
                                   agendamento.status === 'aguardando' ? '#e0e7ff' : '#f3f4f6',
                  color: agendamento.status === 'finalizado' ? '#166534' :
                         agendamento.status === 'cancelado' ? '#991b1b' :
                         agendamento.status === 'falta' ? '#92400e' :
                         agendamento.status === 'em_atendimento' ? '#1e40af' :
                         agendamento.status === 'aguardando' ? '#3730a3' : '#374151'
                }}
              >
                {agendamento.status === 'finalizado' ? 'Finalizado' :
                 agendamento.status === 'cancelado' ? 'Cancelado' :
                 agendamento.status === 'falta' ? 'Falta' :
                 agendamento.status === 'em_atendimento' ? 'Em atendimento' :
                 agendamento.status === 'aguardando' ? 'Aguardando' :
                 agendamento.status === 'agendado' ? 'Agendado' : agendamento.status}
              </Badge>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-[500px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4 px-3 md:px-6">
        <div className="flex flex-col gap-3">
          {/* Navegação da Semana */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 md:gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={irParaSemanaAnterior}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs md:text-sm" onClick={irParaHoje}>
                Hoje
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={irParaProximaSemana}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Date Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-8 px-2 md:px-3 text-xs md:text-sm font-semibold gap-1 md:gap-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  <CalendarIcon className="h-3 w-3 md:h-4 md:w-4" style={{ color: '#4A6B5D' }} />
                  <span className="truncate">
                    {format(currentWeekStart, "dd/MM", { locale: ptBR })} - {format(addDays(currentWeekStart, 5), "dd/MM/yy", { locale: ptBR })}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={currentWeekStart}
                  onSelect={(date) => date && irParaData(date)}
                  locale={ptBR}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Filtro Profissional - Mobile */}
            {!isProfissional && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="md:hidden h-8 px-2 text-xs gap-1"
                  >
                    <Filter className="h-3 w-3" />
                    <span className="truncate max-w-[80px]">
                      {filtroProfissionais.length === 0 
                        ? 'Todos' 
                        : filtroProfissionais.length === 1
                          ? profissionais.find(p => p.id === filtroProfissionais[0])?.usuarios?.nome?.split(' ')[0] || 'Prof.'
                          : `${filtroProfissionais.length} prof.`}
                    </span>
                    {filtroProfissionais.length > 0 && (
                      <Badge variant="secondary" className="h-4 w-4 p-0 text-[10px] flex items-center justify-center rounded-full" style={{ backgroundColor: '#4A6B5D', color: 'white' }}>
                        {filtroProfissionais.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="end">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between pb-2 border-b">
                      <span className="text-sm font-medium">Filtrar Profissional</span>
                      {filtroProfissionais.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs"
                          onClick={() => setFiltroProfissionais([])}
                        >
                          Limpar
                        </Button>
                      )}
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {profissionais.map((prof, index) => (
                        <div
                          key={prof.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                          onClick={() => toggleFiltroProfissional(prof.id)}
                        >
                          <Checkbox 
                            checked={filtroProfissionais.includes(prof.id)}
                            onCheckedChange={() => toggleFiltroProfissional(prof.id)}
                          />
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: prof.cor || CORES_PROFISSIONAIS[index % CORES_PROFISSIONAIS.length] }}
                          />
                          <span className="text-sm leading-tight" style={{ wordBreak: 'break-word' }}>
                            {rotuloProf(prof)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Filtros e Busca - escondido em mobile para economizar espaço */}
          <div className="hidden md:flex flex-wrap items-center gap-2">
            {/* Busca por Paciente */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar paciente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-6 w-6"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Filtros - apenas para Admin/Recepção */}
            {!isProfissional && (
              <>
                {/* Filtro por Sala - Multi-seleção */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-44 justify-between">
                      <span className="truncate">
                        {filtroSalas.length === 0 
                          ? 'Todas as salas' 
                          : filtroSalas.length === 1
                            ? `Sala ${salas.find(s => s.id === filtroSalas[0])?.numero || '?'}`
                            : `${filtroSalas.length} salas`}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="start">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between pb-2 border-b">
                        <span className="text-sm font-medium">Filtrar por Sala</span>
                        {filtroSalas.length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-xs"
                            onClick={() => setFiltroSalas([])}
                          >
                            Limpar
                          </Button>
                        )}
                      </div>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {salas.map(sala => (
                          <div
                            key={sala.id}
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => toggleFiltroSala(sala.id)}
                          >
                            <Checkbox 
                              checked={filtroSalas.includes(sala.id)}
                              onCheckedChange={() => toggleFiltroSala(sala.id)}
                            />
                            <span className="text-sm">
                              <span className="font-semibold">Sala {sala.numero}</span>
                              {sala.nome && sala.nome !== `Sala ${sala.numero}` && (
                                <span className="text-gray-500 ml-1">({sala.nome})</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Filtro por Profissional - Multi-seleção COM BUSCA */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-52 justify-between">
                      <span className="truncate">
                        {filtroProfissionais.length === 0 
                          ? 'Todos profissionais' 
                          : filtroProfissionais.length === 1
                            ? profissionais.find(p => p.id === filtroProfissionais[0])?.usuarios?.nome?.split(' ')[0] || 'Prof.'
                            : `${filtroProfissionais.length} profissionais`}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-2" align="start">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between pb-2 border-b">
                        <span className="text-sm font-medium">Filtrar por Profissional</span>
                        {filtroProfissionais.length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-xs"
                            onClick={() => setFiltroProfissionais([])}
                          >
                            Limpar
                          </Button>
                        )}
                      </div>
                      {/* Campo de busca */}
                      <div className="relative">
                        <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Digite para buscar..."
                          value={buscaProfissional}
                          onChange={(e) => setBuscaProfissional(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          autoComplete="off"
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {profissionaisFiltrados.map(prof => (
                          <div
                            key={prof.id}
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => toggleFiltroProfissional(prof.id)}
                          >
                            <Checkbox 
                              checked={filtroProfissionais.includes(prof.id)}
                              onCheckedChange={() => toggleFiltroProfissional(prof.id)}
                            />
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: prof.cor || CORES_PROFISSIONAIS[0] }}
                            />
                            <span className="text-sm leading-tight" style={{ wordBreak: 'break-word' }}>
                              {rotuloProf(prof)}
                            </span>
                          </div>
                        ))}
                        {profissionaisFiltrados.length === 0 && (
                          <div className="text-sm text-gray-500 text-center py-2">
                            Nenhum profissional encontrado
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Botão limpar filtros */}
                {(filtroSalas.length > 0 || filtroProfissionais.length > 0 || searchTerm) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={limparFiltros}
                    className="text-xs gap-1"
                  >
                    <X className="h-3 w-3" />
                    Limpar filtros
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Navegação mobile entre dias */}
        {isMobile && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={irDiasAnteriores}
              className="px-2"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="ml-1 text-xs">Anterior</span>
            </Button>
            <span className="text-sm font-medium text-gray-600">
              {format(diasSemana[0], 'dd/MM')} - {format(diasSemana[diasSemana.length - 1], 'dd/MM')}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={irDiasProximos}
              className="px-2"
            >
              <span className="mr-1 text-xs">Próximo</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Container do grid com scroll isolado */}
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className={isMobile ? 'min-w-[400px]' : 'min-w-[900px]'}>
            {/* Cabeçalho: Dias + Salas */}
            <div 
              className="grid border-b sticky top-0 bg-white z-20" 
              style={{ gridTemplateColumns: `60px repeat(${diasSemana.length}, 1fr)` }}
            >
              <div 
                className="p-2 border-r bg-gray-50 font-medium text-xs text-gray-500 sticky left-0 z-30"
                style={{ minWidth: '60px' }}
              >
                Horário
              </div>
              {diasSemana.map(dia => (
                <div 
                  key={dia.toISOString()} 
                  className={`p-2 text-center border-r ${isSameDay(dia, new Date()) ? 'bg-emerald-50' : 'bg-gray-50'}`}
                  style={{ minWidth: isMobile ? '100px' : 'auto' }}
                >
                  <div className="font-semibold text-xs md:text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {format(dia, isMobile ? 'EEE' : 'EEEE', { locale: ptBR })}
                  </div>
                  <div className={`text-base md:text-lg ${isSameDay(dia, new Date()) ? 'text-emerald-600 font-bold' : 'text-gray-700'}`}>
                    {format(dia, 'dd')}
                  </div>
                </div>
              ))}
            </div>

            {/* Grid de Horários */}
            <div className="relative">
              {/* Linhas de horário */}
              {horariosSlots.map((slot, index) => (
                <div 
                  key={slot.label}
                  className="grid border-b"
                  style={{ 
                    gridTemplateColumns: `60px repeat(${diasSemana.length}, 1fr)`,
                    height: '40px'
                  }}
                >
                  {/* Coluna de horário - STICKY */}
                  <div 
                    className="p-1 border-r bg-gray-50 text-xs text-gray-500 flex items-start justify-end pr-2 sticky left-0 z-10"
                    style={{ minWidth: '60px' }}
                  >
                    {slot.minuto === 0 && slot.label}
                  </div>
                  
                  {/* Células por dia */}
                  {diasSemana.map(dia => {
                    const diaKey = format(dia, 'yyyy-MM-dd');
                    const { disponivel, slot: slotDisp } = getSlotDisponibilidade(dia, slot.hora, slot.minuto);
                    
                    // Verificar se é uma pausa/intervalo
                    const pausa = getSlotPausa(dia, slot.hora, slot.minuto);
                    // Verificar se está dentro de um Bloqueio de Agenda ativo
                    const bloqueio = getSlotBloqueio(dia, slot.hora, slot.minuto);
                    
                    // Verificar se já tem agendamento neste slot
                    const temAgendamento = agendamentosFiltrados.some(ag => {
                      const agData = parseLocalDateTime(ag.data_hora_inicio);
                      const agFim = parseLocalDateTime(ag.data_hora_fim);
                      if (!agData || !agFim) return false;
                      if (format(agData, 'yyyy-MM-dd') !== diaKey) return false;
                      
                      // Horário do slot atual
                      const slotDate = new Date(dia);
                      slotDate.setHours(slot.hora, slot.minuto, 0, 0);
                      
                      return slotDate >= agData && slotDate < agFim;
                    });
                    
                    // Determinar estilo da célula baseado na disponibilidade
                    let cellClass = '';
                    let cellStyle = {};
                    let isClickable = false;
                    let cellTitle = undefined;
                    
                    if (bloqueio && !temAgendamento) {
                      // Bloqueio de Agenda - vermelho scarlate hachurado (alta prioridade)
                      const motivoTxt = bloqueio.motivo || bloqueio.tipo || 'Bloqueio de agenda';
                      const rangeTxt = bloqueio.dia_inteiro
                        ? 'dia inteiro'
                        : `${(bloqueio.hora_inicio || '').slice(0,5)} - ${(bloqueio.hora_fim || '').slice(0,5)}`;
                      cellStyle = {
                        backgroundColor: 'rgba(255, 36, 0, 0.18)',
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255, 36, 0, 0.55) 5px, rgba(255, 36, 0, 0.55) 9px)'
                      };
                      cellTitle = `🚫 ${motivoTxt} (${rangeTxt}) — ${bloqueio.data_inicio}${bloqueio.data_fim && bloqueio.data_fim !== bloqueio.data_inicio ? ' a ' + bloqueio.data_fim : ''}`;
                    } else if (pausa && !temAgendamento) {
                      // Pausa/Intervalo - laranja claro (indica bloqueio)
                      cellStyle = { backgroundColor: 'rgba(251, 146, 60, 0.35)' }; // Laranja claro (orange-400)
                      cellTitle = `${pausa.descricao || 'Intervalo'} (${(pausa.hora_inicio || '').slice(0,5)} - ${(pausa.hora_fim || '').slice(0,5)})`;
                    } else if (disponivel === true && !temAgendamento) {
                      // Disponível e sem agendamento - verde claro, clicável
                      cellClass = 'cursor-pointer hover:bg-emerald-200 transition-colors';
                      cellStyle = { backgroundColor: 'rgba(16, 185, 129, 0.15)' }; // Verde mais visível
                      isClickable = true;
                      cellTitle = `Clique para agendar às ${slot.hora.toString().padStart(2, '0')}:${slot.minuto.toString().padStart(2, '0')}${slotDisp?.sala?.nome ? ` - ${slotDisp.sala.nome}` : ''}`;
                    } else if (disponivel === false) {
                      // Indisponível - padrão hachurado cinza mais escuro
                      cellStyle = { 
                        backgroundColor: '#e5e7eb',
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(107, 114, 128, 0.2) 4px, rgba(107, 114, 128, 0.2) 8px)'
                      };
                      cellTitle = 'Horário não disponível';
                    }
                    
                    return (
                      <div 
                        key={`${diaKey}-${slot.label}`}
                        className={`border-r relative ${
                          slot.minuto === 0 ? 'border-t border-gray-200' : 'border-t border-gray-100'
                        } ${isSameDay(dia, new Date()) && disponivel !== false ? 'bg-emerald-50/30' : ''} ${cellClass}`}
                        style={{ 
                          minWidth: isMobile ? '100px' : 'auto',
                          ...cellStyle
                        }}
                        onClick={isClickable ? () => handleSlotClick(dia, slot.hora, slot.minuto) : undefined}
                        title={cellTitle}
                      >
                        {/* Renderizar agendamentos apenas na primeira linha do horário de início */}
                        {index === 0 && salas.length === 0 && (
                          <div className="absolute inset-0 pointer-events-none">
                            {(agendamentosPorDiaSala[diaKey]?.['sem_sala'] || []).map(renderAgendamento)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Overlay de agendamentos */}
              <div 
                className="absolute top-0 bottom-0 grid pointer-events-none"
                style={{ 
                  gridTemplateColumns: `repeat(${diasSemana.length}, 1fr)`,
                  left: '60px',
                  right: 0
                }}
              >
                {diasSemana.map(dia => {
                  const diaKey = format(dia, 'yyyy-MM-dd');
                  const agendamentosDoDia = agendamentosFiltrados.filter(ag => {
                    const agData = parseLocalDateTime(ag.data_hora_inicio);
                    if (!agData) return false;
                    return format(agData, 'yyyy-MM-dd') === diaKey;
                  });

                  return (
                    <div key={diaKey} className="relative border-r" style={{ minWidth: isMobile ? '100px' : 'auto' }}>
                      {agendamentosDoDia.map(renderAgendamento)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgendaSemanalView;
